import dayjs from 'dayjs';
import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { useTranslation } from '@gem/modules/i18n';
import dateService from '@gem/services/date-service';

import { getHumanizeRRule } from 'APP/utils/calendar/getHumanizeRRule';
import { getLanguage } from 'APP/utils/getLanguage';

import { IEventCalendar } from '../../Content.types';
import { RepeatEnum, RepeatMap } from '../Form/Form.constants';

interface IInfoPresenter {
  selectedEvent: IEventCalendar | null;
  isToday: boolean;
  isTomorrow: boolean;
  canEditStatus: boolean;
  startFormated: string | null;
  time: string;
  repeatText: string;
  reminderText: string;
  endFormated(date: Date): string | null;
  formatDuration(time: number, unit: string): string;
  setSelectedEvent(selectedEvent: IEventCalendar): void;
  endDate(end: Date, allDay: boolean): Date;
}

interface IInfoPresenterProps {
  selectedEvent: IEventCalendar;
}

export function useInfoPresenter(props: IInfoPresenterProps): IInfoPresenter {
  const { t } = useTranslation();

  const presenter = useLocalObservable<IInfoPresenter>(() => ({
    selectedEvent: null,

    get isToday(): boolean {
      if (!presenter.selectedEvent) {
        return false;
      }

      return dateService.isToday(presenter.selectedEvent.start);
    },

    get isTomorrow(): boolean {
      if (!presenter.selectedEvent) {
        return false;
      }

      return dayjs(presenter.selectedEvent.start).isSame(dayjs().add(1, 'day'), 'day');
    },

    get startFormated(): string | null {
      return dateService.format(presenter.selectedEvent?.start, {
        hour: 'numeric',
        minute: 'numeric',
      });
    },

    get repeatText(): string {
      if (!presenter.selectedEvent) {
        return '';
      }

      return `${t('calendar_event_repeat')} ${
        t(RepeatMap[presenter.selectedEvent.recurrenceType as RepeatEnum]) ||
        getHumanizeRRule(presenter.selectedEvent.recurrenceType)
      }`;
    },

    get reminderText(): string {
      if (!presenter.selectedEvent) {
        return '';
      }

      return presenter.selectedEvent.reminders.length > 0
        ? `${t('calendar_event_reminder')} ${presenter.selectedEvent.reminders
            .map((reminder) => {
              return presenter.formatDuration(reminder.time, reminder.unit);
            })
            .join(', ')}`
        : `${t('calendar_event_no_reminders')}`;
    },

    get canEditStatus(): boolean {
      if (!presenter.selectedEvent) {
        return false;
      }

      return presenter.selectedEvent.participants.some(
        (participant) => participant.email === presenter.selectedEvent?.account.providerEmail
      );
    },

    get time(): string {
      if (!presenter.selectedEvent) {
        return '';
      }

      if (presenter.isToday) {
        return `${t('calendar_todayat')} ${presenter.startFormated} - ${presenter.endFormated(
          presenter.endDate(presenter.selectedEvent.end, presenter.selectedEvent.allDay)
        )}`;
      }

      if (presenter.isTomorrow) {
        return `${t('calendar_tomorrow')} ${presenter.startFormated} - ${presenter.endFormated(
          presenter.endDate(presenter.selectedEvent.end, presenter.selectedEvent.allDay)
        )}`;
      }

      let result = new Intl.DateTimeFormat(getLanguage(), {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        hour: 'numeric',
        minute: 'numeric',
      }).formatRange(
        presenter.selectedEvent.start,
        presenter.endDate(presenter.selectedEvent.end, presenter.selectedEvent.allDay)
      );

      result = result.charAt(0).toUpperCase() + result.slice(1);

      return result;
    },

    setSelectedEvent(selectedEvent: IEventCalendar): void {
      presenter.selectedEvent = selectedEvent;
    },

    endDate(end: Date, allDay: boolean): Date {
      return allDay ? new Date(end.getTime() - 1) : end;
    },

    endFormated(date: Date): string | null {
      return dateService.format(date, {
        hour: 'numeric',
        minute: 'numeric',
      });
    },

    formatDuration(time: number, unit: 'minutes' | 'hours' | 'days' | 'weeks'): string {
      if (time === 0) {
        return t('calendar_reminder_at_event');
      }

      const rtf = new Intl.RelativeTimeFormat(getLanguage(), {
        numeric: 'always',
        style: 'long',
      });

      const relativeString = rtf.format(time, unit);

      return relativeString.replace('через', 'за').replace(/^in /, 'for ');
    },
  }));

  useEffect(() => {
    presenter.setSelectedEvent(props.selectedEvent);
  }, [props.selectedEvent]);

  return presenter;
}
